import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useToast } from "@/hooks/use-toast";
import { Loader2, DollarSign } from "lucide-react";

import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Card, CardContent } from "@/components/ui/card";

const donationFormSchema = z.object({
  donationType: z.enum(["one-time", "monthly"], {
    required_error: "Please select a donation type.",
  }),
  amount: z.enum(["25", "50", "100", "250", "custom"], {
    required_error: "Please select an amount.",
  }),
  customAmount: z.string().optional()
    .refine(val => !val || parseFloat(val) >= 1, {
      message: "Custom amount must be at least $1.",
    }),
  name: z.string().min(2, {
    message: "Name must be at least 2 characters.",
  }),
  email: z.string().email({
    message: "Please enter a valid email address.",
  }),
  honorOf: z.string().optional(),
  message: z.string().optional(),
  isAnonymous: z.boolean().default(false),
});

type DonationFormValues = z.infer<typeof donationFormSchema>;

export default function DonationForm() {
  const { toast } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSubmitSuccess, setIsSubmitSuccess] = useState(false);

  const form = useForm<DonationFormValues>({
    resolver: zodResolver(donationFormSchema),
    defaultValues: {
      donationType: "one-time",
      amount: "50",
      customAmount: "",
      name: "",
      email: "",
      honorOf: "",
      message: "",
      isAnonymous: false,
    },
  });

  const watchAmount = form.watch("amount");
  const watchDonationType = form.watch("donationType");

  // This would connect to a payment processor like Stripe in a real implementation
  function onSubmit(data: DonationFormValues) {
    setIsProcessing(true);
    
    // Simulate payment processing
    setTimeout(() => {
      setIsProcessing(false);
      setIsSubmitSuccess(true);
      
      toast({
        title: "Thank you for your donation!",
        description: `Your ${data.donationType} donation helps us continue our mission.`,
      });
    }, 2000);
  }

  if (isSubmitSuccess) {
    return (
      <div className="bg-white p-8 rounded-lg shadow-md max-w-2xl mx-auto">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 mb-6 rounded-full bg-green-100">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Thank You for Your Generosity!</h2>
          <p className="text-lg text-slate-600 mb-6">
            Your donation will help individuals overcome barriers and build new futures. You'll receive a receipt via email shortly.
          </p>
          <Button asChild>
            <a href="/">Return to Home</a>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white p-8 rounded-lg shadow-md max-w-2xl mx-auto">
      <h2 className="text-2xl font-bold mb-6 text-slate-900 text-center">Support Our Mission</h2>
      <p className="mb-6 text-slate-600 text-center">
        Your donation helps us provide essential services to individuals working to overcome barriers to employment and stability.
      </p>

      <Card className="bg-primary-50 mb-8">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
            <div className="p-4">
              <div className="text-3xl font-bold text-primary-700">$50</div>
              <p className="text-slate-600">Provides job readiness training for one client</p>
            </div>
            <div className="p-4">
              <div className="text-3xl font-bold text-primary-700">$100</div>
              <p className="text-slate-600">Sponsors a certification program</p>
            </div>
            <div className="p-4">
              <div className="text-3xl font-bold text-primary-700">$250</div>
              <p className="text-slate-600">Funds a month of case management</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          <FormField
            control={form.control}
            name="donationType"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Donation Type</FormLabel>
                <FormControl>
                  <RadioGroup
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                    className="flex flex-row space-x-4"
                  >
                    <FormItem className="flex items-center space-x-2">
                      <FormControl>
                        <RadioGroupItem value="one-time" />
                      </FormControl>
                      <FormLabel className="font-normal cursor-pointer">
                        One-time
                      </FormLabel>
                    </FormItem>
                    <FormItem className="flex items-center space-x-2">
                      <FormControl>
                        <RadioGroupItem value="monthly" />
                      </FormControl>
                      <FormLabel className="font-normal cursor-pointer">
                        Monthly
                      </FormLabel>
                    </FormItem>
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="amount"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Donation Amount</FormLabel>
                <FormControl>
                  <RadioGroup
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                    className="grid grid-cols-2 sm:grid-cols-5 gap-4"
                  >
                    <FormItem className="flex items-center space-x-2">
                      <FormControl>
                        <RadioGroupItem value="25" className="sr-only" />
                      </FormControl>
                      <FormLabel className={`w-full py-2 px-4 rounded-md border text-center cursor-pointer transition-colors ${
                        field.value === "25" ? "bg-primary-100 border-primary-500 text-primary-700" : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50"
                      }`}>
                        $25
                      </FormLabel>
                    </FormItem>
                    <FormItem className="flex items-center space-x-2">
                      <FormControl>
                        <RadioGroupItem value="50" className="sr-only" />
                      </FormControl>
                      <FormLabel className={`w-full py-2 px-4 rounded-md border text-center cursor-pointer transition-colors ${
                        field.value === "50" ? "bg-primary-100 border-primary-500 text-primary-700" : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50"
                      }`}>
                        $50
                      </FormLabel>
                    </FormItem>
                    <FormItem className="flex items-center space-x-2">
                      <FormControl>
                        <RadioGroupItem value="100" className="sr-only" />
                      </FormControl>
                      <FormLabel className={`w-full py-2 px-4 rounded-md border text-center cursor-pointer transition-colors ${
                        field.value === "100" ? "bg-primary-100 border-primary-500 text-primary-700" : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50"
                      }`}>
                        $100
                      </FormLabel>
                    </FormItem>
                    <FormItem className="flex items-center space-x-2">
                      <FormControl>
                        <RadioGroupItem value="250" className="sr-only" />
                      </FormControl>
                      <FormLabel className={`w-full py-2 px-4 rounded-md border text-center cursor-pointer transition-colors ${
                        field.value === "250" ? "bg-primary-100 border-primary-500 text-primary-700" : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50"
                      }`}>
                        $250
                      </FormLabel>
                    </FormItem>
                    <FormItem className="flex items-center space-x-2">
                      <FormControl>
                        <RadioGroupItem value="custom" className="sr-only" />
                      </FormControl>
                      <FormLabel className={`w-full py-2 px-4 rounded-md border text-center cursor-pointer transition-colors ${
                        field.value === "custom" ? "bg-primary-100 border-primary-500 text-primary-700" : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50"
                      }`}>
                        Custom
                      </FormLabel>
                    </FormItem>
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {watchAmount === "custom" && (
            <FormField
              control={form.control}
              name="customAmount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Custom Amount</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 h-4 w-4" />
                      <Input placeholder="Enter amount" className="pl-9" {...field} />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Full Name</FormLabel>
                <FormControl>
                  <Input placeholder="Your name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input placeholder="your.email@example.com" type="email" {...field} />
                </FormControl>
                <FormDescription>
                  We'll send a receipt to this email address.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="honorOf"
            render={({ field }) => (
              <FormItem>
                <FormLabel>In Honor Of (Optional)</FormLabel>
                <FormControl>
                  <Input placeholder="Name of person you're honoring" {...field} />
                </FormControl>
                <FormDescription>
                  Make this donation in honor or memory of someone special.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="message"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Message (Optional)</FormLabel>
                <FormControl>
                  <Textarea placeholder="Your message..." rows={3} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="isAnonymous"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel>
                    Make this donation anonymous
                  </FormLabel>
                  <FormDescription>
                    Your name will not be publicly displayed in our donor list.
                  </FormDescription>
                </div>
              </FormItem>
            )}
          />

          <Button 
            type="submit" 
            className="w-full"
            disabled={isProcessing}
          >
            {isProcessing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : (
              `Donate ${watchAmount === "custom" ? form.watch("customAmount") ? `$${form.watch("customAmount")}` : "" : `$${watchAmount}`} ${watchDonationType === "monthly" ? "Monthly" : ""}`
            )}
          </Button>
        </form>
      </Form>

      <div className="mt-8 text-center text-sm text-slate-500">
        <p>Your donation is tax-deductible to the extent allowed by law.</p>
        <p className="mt-2">StaffAbility is a 501(c)(3) nonprofit organization.</p>
      </div>
    </div>
  );
}
